/*
 * Cuentas: tabla por defecto, tablero opcional. El filtro y el orden viajan en la
 * URL para que una vista se pueda pegar en WhatsApp.
 */

import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useCuentas } from "../api/hooks";
import { ETAPAS, FASES, nombreEquipo, type Cuenta, type Etapa } from "../api/tipos";
import { useFormularios } from "../shell/Formularios";
import { BarraFase, Esqueleto, Fit, MarcaRelacional, PastillaEtapa, fmtFecha } from "../ui/primitivas";

type Col = { id: keyof Cuenta | "ultimo"; label: string; w: string; derecha?: boolean };
const COLS: Col[] = [
  { id: "empresa", label: "Empresa", w: "minmax(200px,1.6fr)" },
  { id: "etapa", label: "Etapa", w: "150px" },
  { id: "fase", label: "Fase", w: "160px" },
  { id: "ultimo", label: "Último contacto", w: "120px" },
  { id: "proxima_accion", label: "Próxima acción", w: "minmax(180px,2fr)" },
  { id: "fit", label: "Fit", w: "52px", derecha: true },
  { id: "responsable_id", label: "Resp.", w: "64px" },
];
const ORDEN_ETAPA = Object.keys(ETAPAS) as Etapa[];

export function Cuentas() {
  const nav = useNavigate();
  const { abrir } = useFormularios();
  const { data: todas, isLoading } = useCuentas();
  const [sp, setSp] = useSearchParams();
  const vista = sp.get("vista") === "tablero" ? "tablero" : "tabla";
  const q = sp.get("q") ?? "";
  const etapaF = (sp.get("etapa") as Etapa | null) ?? "todas";
  const [sortCol, sortDir] = (sp.get("orden") ?? "fit:-1").split(":") as [Col["id"], string];
  const dir = Number(sortDir) || -1;
  const set = (k: string, v: string | null) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };

  const filas = useMemo(() => {
    if (!todas) return [];
    const ql = q.toLowerCase();
    const clave = (c: Cuenta): string | number => {
      switch (sortCol) {
        case "empresa": return c.empresa.toLowerCase();
        case "etapa": return ORDEN_ETAPA.indexOf(c.etapa);
        case "fase": return c.fase;
        case "ultimo": return c.dias_sin_contacto ?? 999;
        case "proxima_accion": return c.proxima_accion.toLowerCase();
        case "fit": return c.fit;
        case "responsable_id": return c.responsable_id;
        default: return 0;
      }
    };
    return todas
      .filter((c) => (etapaF === "todas" || c.etapa === etapaF) && (!ql || [c.empresa, c.contacto, c.giro, c.ubicacion].join(" ").toLowerCase().includes(ql)))
      .sort((a, b) => { const x = clave(a), y = clave(b); return (x < y ? -1 : x > y ? 1 : 0) * dir; });
  }, [todas, q, etapaF, sortCol, dir]);

  const ordenar = (col: Col["id"]) => set("orden", sortCol === col ? `${col}:${-dir}` : `${col}:${col === "fit" || col === "fase" ? -1 : 1}`);
  const grid = COLS.map((c) => c.w).join(" ");

  return (
    <div className="pagina" style={{ maxWidth: 1280 }}>
      <header style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
        <h1 className="titulo" style={{ display: "flex", alignItems: "baseline", gap: 8 }}>Cuentas <span style={{ color: "var(--muted)", fontWeight: 500, fontSize: 14, fontFamily: "var(--font-body)", letterSpacing: 0 }}>{filas.length} de {todas?.length ?? 0}</span></h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <div className="seg">
            {(["tabla", "tablero"] as const).map((v) => <button key={v} className={vista === v ? "activo" : ""} onClick={() => set("vista", v === "tabla" ? null : v)} style={{ textTransform: "capitalize" }}>{v}</button>)}
          </div>
          <button className="btn btn-primario" onClick={() => abrir({ tipo: "cuenta" })}>+ Nueva cuenta</button>
        </div>
      </header>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <input value={q} onChange={(e) => set("q", e.target.value || null)} placeholder="Filtrar por empresa, contacto, giro…" aria-label="Filtrar"
          style={{ width: 260, padding: "6px 10px", border: "1px solid var(--border)", borderRadius: "var(--r-control)", background: "var(--card)", fontSize: 13, outline: "none" }} />
        <span style={{ width: 1, height: 20, background: "var(--border)", margin: "0 4px" }} />
        <button className={"chip" + (etapaF === "todas" ? " activo" : "")} onClick={() => set("etapa", null)}>Todas</button>
        {ORDEN_ETAPA.map((k) => <button key={k} className={"chip" + (etapaF === k ? " activo" : "")} onClick={() => set("etapa", etapaF === k ? null : k)}>{ETAPAS[k].label}</button>)}
      </div>

      {isLoading && <div className="tarjeta"><Esqueleto lineas={6} /></div>}

      {!isLoading && vista === "tabla" && (
        <div className="lista" style={{ overflowX: "auto" }}>
          <div style={{ minWidth: 1000 }}>
            <div style={{ display: "grid", gridTemplateColumns: grid, gap: 16, padding: "0 18px", height: 36, alignItems: "center", borderBottom: "1px solid var(--border)", background: "var(--page)" }}>
              {COLS.map((c) => (
                <button key={c.id} onClick={() => ordenar(c.id)} className="tag11" style={{ color: sortCol === c.id ? "var(--text)" : "var(--muted)", display: "flex", gap: 4, justifyContent: c.derecha ? "flex-end" : "flex-start", alignItems: "center" }}>
                  {c.label}{sortCol === c.id && <span style={{ fontSize: 9 }}>{dir === 1 ? "▲" : "▼"}</span>}
                </button>
              ))}
            </div>
            {filas.map((c) => (
              <div key={c.id} role="link" tabIndex={0} onClick={() => nav(`/cuentas/${c.id}`)} onKeyDown={(e) => e.key === "Enter" && nav(`/cuentas/${c.id}`)} className="fila-hover"
                style={{ display: "grid", gridTemplateColumns: grid, gap: 16, padding: "0 18px", minHeight: "var(--row-h)", alignItems: "center", cursor: "pointer", fontSize: 13.5, borderRadius: 0 }}>
                <span style={{ minWidth: 0, display: "flex", flexDirection: "column", padding: "7px 0" }}>
                  <span style={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.empresa}<MarcaRelacional show={c.relacional} /></span>
                  <span style={{ fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.contacto}{c.ubicacion ? ` · ${c.ubicacion}` : ""}</span>
                </span>
                <span><PastillaEtapa etapa={c.etapa} /></span>
                <BarraFase fase={c.fase} />
                <span className="mono" style={{ color: (c.dias_sin_contacto ?? 0) >= 12 ? "var(--risk)" : c.dias_sin_contacto === null ? "var(--faint)" : "var(--muted)" }}>
                  {fmtFecha(c.ultimo_contacto)}{c.dias_sin_contacto !== null && <span style={{ color: "var(--faint)" }}> · {c.dias_sin_contacto}d</span>}
                </span>
                <span style={{ fontSize: 13, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{c.proxima_accion}</span>
                <span style={{ textAlign: "right" }}><Fit v={c.fit} /></span>
                <span style={{ color: "var(--muted)", fontSize: 12.5 }}>{nombreEquipo(c.responsable_id)}</span>
              </div>
            ))}
            {filas.length === 0 && <div style={{ padding: 28, textAlign: "center", color: "var(--faint)" }}>Ninguna cuenta coincide.</div>}
          </div>
        </div>
      )}

      {!isLoading && vista === "tablero" && (
        <div style={{ overflowX: "auto", paddingBottom: 8 }}>
          <div style={{ display: "grid", gridAutoFlow: "column", gridAutoColumns: 220, gap: 12 }}>
            {FASES.map((f, fi) => {
              const col = filas.filter((c) => c.fase === fi);
              return (
                <div key={f.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "0 4px 8px" }}>
                    <span style={{ fontSize: 13, fontWeight: 500, whiteSpace: "nowrap" }}><span className="mono" style={{ color: "var(--sky)", marginRight: 6 }}>{f.id}</span>{f.nombre}</span>
                    <span className="mono" style={{ color: "var(--faint)" }}>{col.length}</span>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8, minHeight: 80, padding: 6, background: "var(--sunken)", borderRadius: "var(--r-card)" }}>
                    {col.map((c) => (
                      <button key={c.id} onClick={() => nav(`/cuentas/${c.id}`)} style={{ background: "var(--card)", border: "1px solid var(--border)", borderRadius: "var(--r-control)", padding: "10px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
                        <span style={{ fontWeight: 500, fontSize: 13.5, lineHeight: 1.3 }}>{c.empresa}<MarcaRelacional show={c.relacional} /></span>
                        <span style={{ fontSize: 12, color: "var(--muted)", lineHeight: 1.35 }}>{c.contacto}</span>
                        <span style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", marginTop: 2 }}><PastillaEtapa etapa={c.etapa} /><Fit v={c.fit} /></span>
                      </button>
                    ))}
                    {col.length === 0 && <div className="abierto" style={{ height: 64, borderRadius: "var(--r-control)" }} />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      <p style={{ marginTop: 14, fontSize: 12, color: "var(--faint)", display: "flex", gap: 14 }}>
        <span><MarcaRelacional show /> origen relacional — fuera de métricas de conversión</span><span>Fit ≥ 75 en verde</span>
      </p>
    </div>
  );
}
